import { HttpClient, HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { SaveCaseInput } from '../models/save-case-input.model';
import { SpinnerService } from './spinner.service';
import * as FileSaver from "file-saver";
import { BehaviorSubject, Observable } from 'rxjs';
import { LetterModel } from '../models/letter.model';
import { CaseModel } from '../models/case.model';
import { CaseDocumentFile } from '../models/case-document-file.model';
import { CaseDocumentModel } from '../models/case-document.model';
import { AuthenticationService } from './authentication.service';
import { SaveCase } from '../models/save-case.model';

@Injectable({
  providedIn: 'root'
})
export class CaseService {

  private _currentCaseSubject: BehaviorSubject<CaseModel | null> = new BehaviorSubject<CaseModel | null>(null);
  public currentCase: Observable<CaseModel | null> = this._currentCaseSubject.asObservable();

  constructor(
    private http: HttpClient,
    private authenticationService: AuthenticationService,
    private spinnerService: SpinnerService) {
  }

  loadPostalPlace(value: string) {
    return this.http.get('/api/form/getpostalplace/' + value, { responseType: "text" });
  }

  loadCase(caseUId: string) {
    this.http.get<CaseModel>('/api/case/' + caseUId)
      .subscribe(
        (data: CaseModel) => {
          if (data !== undefined) {
            this._currentCaseSubject.next(data);
          }
        },
        (error: HttpErrorResponse) => {
          throw error;
        },
        () => {
          console.log('Finished loading case');
        }
      );
  }

  loadAll() {
    return this.http.get<CaseModel[]>('/api/case/list');
  }

  save(inData: CaseModel) {
    return this.http.post<CaseModel>('/api/case/save', inData)
  }

  saveNewCase(saveCaseInput: SaveCaseInput) {
    let url: string = '/api/form/savecase';
    return this.http.post<SaveCase>(url, saveCaseInput)
  }

  saveFeedback(feedback: any) {
    return this.http.post('/api/feedback/save', feedback);
  }

  sendLetter(letter: LetterModel) {
    return this.http.post('/api/case/sendletter', letter)
  }

  closeApplication() {

    window.location.href = 'https://www.finkn.no';
  }

  setConsent(value: boolean, caseUId: string) {

    let consentData = {
      Value: value,
      CaseUId: caseUId
    }

    return this.http.post<CaseModel>('/api/case/saveconsent', consentData);
  }

  openDocument(caseDocument: CaseDocumentModel, caseUid: string) {
    this.http.get('/api/document/download/casedocument/' + caseDocument.Id + '/' + caseUid, { observe: 'response', responseType: 'arraybuffer' })
      .subscribe(
        {
          next: (response: HttpResponse<ArrayBuffer>) => {
            this.openDocumentInNewTab(response)
          },
          error: (error: HttpErrorResponse) => {
            console.log(error);
            this.spinnerService.hide();
          },
          complete: () => {
            console.log('Finished open file by attachmentId');
            this.spinnerService.hide();
          }
        }
      );
  }

  /* download selected documents from documents-list */
  downloadCaseFiles(documentIds: string[], caseUid: string): void {

    this.spinnerService.show('Vennligst vent, laster ned filer..');

    let selectedFilesInput = {
      CaseUid: caseUid,
      SelectedCaseDocumentIds: documentIds
    }

    let url: string = '/api/document/download/casedocuments/selected';
    this.http.post<CaseDocumentFile[]>(url, selectedFilesInput)
      .subscribe(
        (response: CaseDocumentFile[]) => {
          for (var i = 0; i < response.length; i++) {
            var item = response[i];
            this.downloadCaseDocumentFile(item);
          }
        },
        (error: HttpErrorResponse) => {
          console.log(error);
        },
        () => {
          console.log('Finished download casefiles');
          this.spinnerService.hide();
        }
      );
  }

  openAllByEventId(eventId: string, caseUid: string) {

    this.http.get('/api/document/openletter/' + eventId + '/' + caseUid, { observe: 'response', responseType: 'arraybuffer' })
      .subscribe(
        {
          next: (response: HttpResponse<ArrayBuffer>) => {
            this.openDocumentInNewTab(response);
          },
          error: (error: HttpErrorResponse) => {
            console.log(error);
            this.spinnerService.hide();
          },
          complete: () => {
            console.log('Finished open all files by attachment group');
            this.spinnerService.hide();
          }
        }
      );
  }

  private openDocumentInNewTab(response: HttpResponse<ArrayBuffer>) {

    if (response.body !== null) {
      //this.openDocumentInNewTab(response);
      var contentType: string | null = response.headers.get('content-type');

      if (contentType !== null) {
        var filename = this.getFileNameFromContentDisposition(response);

        if (filename !== null)
        {
          if (contentType == 'application/pdf') {
            this.openFileResponse(response.body, 'application/pdf', filename);
          }
          else {
            // Unknown filetype, lets just download it.. 
            var data = new Blob([response.body], { type: contentType });
            if (filename !== null) {
              FileSaver.saveAs(data, filename);
            }
          }  
        }
      }
    }

    // if (response.body && response.headers) {

    //   let fileName: string | null = this.getFileNameFromContentDisposition(response);
    //   let mimeType: string | null = response.headers.get('content-type');

    //   if (fileName === null) {
    //     return;
    //   }

    //   if (mimeType === null) {
    //     mimeType = 'application/octet-stream';
    //   }

    //   var file = new Blob([response.body], { type: mimeType });
    //   var fileURL = window.URL.createObjectURL(file);
    //   let result = window.open(fileURL);

    //   if (result === null) {
    //     if (confirm("Det ser ut til at nettleseren din blokkerer forgrunnsvinduer, vi kan derfor ikke åpne dokumentet i en ny fane. Ønsker du heller å laste ned dokumentet?")) {
    //       this.saveDocument(fileName, response);
    //     }
    //   }

    // }
  }

  private openFileResponse(body: ArrayBuffer, contentType: string, fileName: string): void {
    
    var b = new Blob([body], { type: contentType });
    const fileURL = URL.createObjectURL(b);

    var openResult = window.open(fileURL, '_blank');
    if (openResult === null)
    {
      FileSaver.saveAs(b, fileName);
    }
  }

  private downloadCaseDocumentFile(item: CaseDocumentFile) {
    var d = this.base64ToArrayBuffer(item.FileContent);
    var b = new Blob([d]); //, { type: item.MimeType });
    //var data = this.b64toBlob(item.FileContent, 'application/pdf');
    FileSaver.saveAs(b, item.FileName);
  }

  private getFileNameFromContentDisposition(response: HttpResponse<ArrayBuffer>): string | null {
    var filename: string | null = null;

    var contentDisposition = response.headers.get('content-disposition')
    if (contentDisposition) {
      const fileNameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
      const matches = fileNameRegex.exec(contentDisposition);
      if (matches != null && matches[1]) {
        filename = matches[1].replace(/['"]/g, '');
        filename = decodeURIComponent(filename);
      }
    }
    return filename;
  }
  // private getFileNameFromContentDisposition(headers: HttpHeaders): string | null {

  //   //attachment; filename="240710 Klageskjema - 202400048.pdf"; filename*=UTF-8''240710%20Klageskjema%20-%20202400048.pdf
  //   var filename = null;

  //   var contentDisposition: string | null = headers.get('content-disposition');
  //   var elements:string[] | undefined = contentDisposition?.split(';')
  //   if (elements && elements.length > 0) {
  //     var fileNameElement = elements.find(e => e.trim().startsWith('filename='));
  //     if (fileNameElement) {
  //       var fileNameElements: string[] | undefined = fileNameElement.split('=');
  //       if (fileNameElements.length === 2) {
  //         filename = fileNameElements[1].replace('"', '');
  //       }
  //     }
  //   }
  //   return filename !== null ? decodeURIComponent(filename) : filename;
  // }
  private base64ToArrayBuffer(base64: any): ArrayBuffer {
    var binary_string = window.atob(base64);
    var len = binary_string.length;
    var bytes = new Uint8Array(len);
    for (var i = 0; i < len; i++) {
      bytes[i] = binary_string.charCodeAt(i);
    }
    return bytes.buffer;
  }
}
